import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Shield, 
  TrendingUp,
  MessageCircle,
  Search,
  CheckCircle2,
  X,
  Phone,
  Clock,
  Award,
  Target,
  Zap,
  Users,
  MapPin,
  AlertCircle,
  BarChart3,
  ChevronRight,
  Star,
  Sparkles,
  Rocket,
  ThumbsUp
} from 'lucide-react';
import ContactForm from '../components/ContactForm';
import TrustedBy from '../components/TrustedBy';
import Stats from '../components/Stats';

const Home = () => {
  const scrollToContact = () => {
    document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section id="home" className="relative pt-32 pb-20 px-6 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/hero1.jpg" 
            alt="Swiss Craftsman" 
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-50 via-white to-purple-50"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="bg-gradient-to-r from-cyan-600 to-purple-600 text-white hover:from-cyan-700 hover:to-purple-700 px-6 py-3 text-base font-bold mb-6 shadow-xl animate-pulse">
              ⭐ Spezialisiert auf Schweizer Handwerksbetriebe
            </Badge>
            <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6">
              🏗️ Webseiten für Schweizer Handwerker,<br />
              <span className="bg-gradient-to-r from-cyan-600 to-purple-600 bg-clip-text text-transparent">
                die täglich neue Anfragen bringen.
              </span>
            </h1>
            <p className="text-3xl text-gray-700 mb-4 font-semibold">
              ✨ Professionell. 🇨🇭 Lokal. 🚀 Erfolgreich.
            </p>
            <p className="text-xl text-gray-600 mb-8">
              Mehr Kunden. Mehr Umsatz. Mehr Erfolg in Ihrer Region.
            </p>
            <Button 
              onClick={scrollToContact}
              size="lg"
              className="bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700 text-white px-12 py-8 text-2xl rounded-xl shadow-2xl hover:shadow-cyan-500/50 transition-all transform hover:scale-105 mb-8"
            >
              <Rocket className="w-7 h-7 mr-3" />
              💼 Kostenlose Offerte anfordern
            </Button>
            <p className="text-sm text-gray-500 mb-8">
              ⚡ Begrenzte Kapazitäten - Jetzt Platz sichern!
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
              {[
                { icon: Shield, text: '💰 Einmalpreis – keine versteckten Kosten', color: 'from-green-500 to-emerald-600' },
                { icon: Search, text: '📈 SEO inklusive', color: 'from-blue-500 to-cyan-600' },
                { icon: MapPin, text: '🎯 Optimiert für lokale Sichtbarkeit', color: 'from-purple-500 to-pink-600' },
                { icon: Target, text: '📱 Mobile & Conversion-optimiert', color: 'from-orange-500 to-red-600' }
              ].map((item, idx) => (
                <div key={idx} className={`flex flex-col items-center gap-3 bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all transform hover:scale-105 border-2 border-transparent hover:border-cyan-300`}>
                  <div className={`w-14 h-14 bg-gradient-to-br ${item.color} rounded-full flex items-center justify-center shadow-lg`}>
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-700 text-center">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <TrustedBy />

      {/* Stats Section */}
      <Stats />

      {/* Problem Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-red-50 to-orange-50 relative overflow-hidden">
        <div className="absolute top-10 right-10 text-9xl opacity-10">❌</div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-red-500 to-red-600 rounded-full mb-6 shadow-2xl">
              <AlertCircle className="w-14 h-14 text-white" />
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              ⚠️ Wenn Ihre Website keine Anfragen bringt,<br />
              <span className="text-red-600">kostet sie Sie täglich Geld. 💸</span>
            </h2>
          </div>
          <div className="max-w-5xl mx-auto">
            <p className="text-2xl text-gray-700 mb-8 text-center font-semibold">
              😰 Viele Schweizer Handwerksbetriebe haben:
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {[
                { icon: '🕸️', text: 'Eine alte Website ohne Wirkung', stat: '65% der Handwerker' },
                { icon: '📉', text: 'Kaum Anfragen über Google', stat: '70% haben keine Anfragen' },
                { icon: '🎯', text: 'Schlechte Platzierungen in der Region', stat: 'Seite 3+ bei Google' },
                { icon: '😤', text: 'Eine Konkurrenz, die online dominiert', stat: 'Verlust von 30% Kunden' }
              ].map((problem, idx) => (
                <Card key={idx} className="border-3 border-red-300 bg-white hover:shadow-2xl transition-all transform hover:scale-105">
                  <CardContent className="p-8 flex items-start gap-4">
                    <div className="text-5xl">{problem.icon}</div>
                    <div className="flex-1">
                      <p className="text-xl font-bold text-gray-900 mb-2">{problem.text}</p>
                      <p className="text-sm text-red-600 font-semibold">{problem.stat}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="relative">
              <div className="absolute -top-6 -left-6 text-6xl">⚡</div>
              <Card className="bg-gradient-to-br from-red-600 to-red-700 text-white border-4 border-red-800 shadow-2xl">
                <CardContent className="p-10">
                  <h3 className="text-3xl font-bold mb-4">💥 Das Resultat?</h3>
                  <p className="text-2xl mb-4 font-semibold">
                    Potenzielle Kunden finden nicht <span className="underline">Sie</span>, sondern Ihren Mitbewerber. 😡
                  </p>
                  <p className="text-xl opacity-90">
                    Und wer online nicht sichtbar ist, existiert für viele Kunden schlicht nicht. 👻
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Search Volume Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-600 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 text-9xl opacity-10">🔍</div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-6 shadow-2xl">
            <Search className="w-10 h-10 text-cyan-600" />
          </div>
          <h2 className="text-5xl font-bold mb-8">
            🇨🇭 In der Schweiz suchen <span className="underline">täglich tausende</span> Menschen nach:
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { search: '„Maler Zürich"', searches: '850+ Suchen/Monat', icon: '🎨' },
              { search: '„Sanitär Notdienst Bern"', searches: '620+ Suchen/Monat', icon: '🚰' },
              { search: '„Elektriker Winterthur"', searches: '740+ Suchen/Monat', icon: '⚡' }
            ].map((item, idx) => (
              <Card key={idx} className="bg-white/10 backdrop-blur-lg border-2 border-white/30 hover:bg-white/20 transition-all transform hover:scale-110 shadow-2xl">
                <CardContent className="p-8">
                  <div className="text-6xl mb-4">{item.icon}</div>
                  <Search className="w-10 h-10 mx-auto mb-4 opacity-80" />
                  <p className="text-2xl font-bold mb-2">{item.search}</p>
                  <p className="text-sm opacity-90 font-semibold">{item.searches}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="p-10 bg-white/10 backdrop-blur-lg rounded-2xl border-4 border-white/30 shadow-2xl">
            <div className="text-7xl mb-4">🤔</div>
            <h3 className="text-4xl font-bold mb-6">Die Frage ist:</h3>
            <p className="text-3xl font-bold">
              Werden <span className="bg-white text-cyan-600 px-4 py-2 rounded-lg">Sie</span> gefunden – oder Ihre Konkurrenz? 🏆
            </p>
          </div>
        </div>
      </section>

      {/* USP Section */}
      <section className="py-20 px-6 relative">
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/craftsman1.jpg" 
            alt="Professional Craftsman" 
            className="w-full h-full object-cover opacity-5"
          />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <Sparkles className="w-16 h-16 text-cyan-600 mx-auto mb-4" />
            <h2 className="text-5xl font-bold text-gray-900 mb-4">
              💎 Wir bauen Webseiten, die <span className="bg-gradient-to-r from-cyan-600 to-purple-600 bg-clip-text text-transparent">verkaufen</span>.<br />
              Nicht nur gut aussehen. ✨
            </h2>
          </div>
          <div className="max-w-4xl mx-auto mb-16">
            <Card className="border-4 border-cyan-400 bg-gradient-to-br from-cyan-50 to-purple-50 shadow-2xl">
              <CardContent className="p-10">
                <div className="text-center mb-6">
                  <div className="text-6xl mb-4">🎯</div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">
                    Spezialisierung auf Schweizer Handwerker.
                  </h3>
                </div>
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  {[
                    { text: '❌ Keine Coiffeursalons.', icon: '💇' },
                    { text: '❌ Keine Start-ups.', icon: '💼' },
                    { text: '❌ Keine Onlineshops.', icon: '🛒' }
                  ].map((text, idx) => (
                    <div key={idx} className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl shadow-md">
                      <div className="text-4xl">{text.icon}</div>
                      <span className="text-gray-700 font-bold text-center">{text.text}</span>
                    </div>
                  ))}
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-cyan-700 mb-4">
                    ✅ Wir arbeiten <span className="underline">ausschliesslich</span> mit Schweizer Handwerksbetrieben! 🇨🇭
                  </p>
                  <div className="flex justify-center gap-4 text-5xl">
                    🔨 🔧 ⚡ 🎨 🚰 🏗️
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="max-w-3xl mx-auto mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-8 text-center">🤔 Warum?</h3>
            <div className="space-y-6">
              {[
                { icon: '💼', text: 'Weil ein Sanitär anders verkauft als ein Treuhänder.' },
                { icon: '🎯', text: 'Weil jede Branche die richtigen Kunden braucht – nicht dieselben.' },
                { icon: '🇨🇭', text: 'Weil lokale Sichtbarkeit in der Schweiz anders funktioniert.' }
              ].map((reason, idx) => (
                <Card key={idx} className="hover:shadow-xl transition-all border-l-4 border-cyan-600">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="text-5xl">{reason.icon}</div>
                    <p className="text-xl text-gray-700 font-semibold">{reason.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="vorteile" className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Was uns <span className="text-cyan-600">unterscheidet</span>
            </h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            {[
              {
                icon: MapPin,
                title: 'Lokales SEO inklusive',
                description: 'Wir optimieren Ihre Website gezielt auf Ihre Region. Damit Sie bei „[Ihre Dienstleistung] + Stadt" sichtbar sind.'
              },
              {
                icon: Target,
                title: 'Verkaufspsychologische Struktur',
                description: 'Klare Botschaften. Klare Vorteile. Klare Handlungsaufforderung.'
              },
              {
                icon: Phone,
                title: 'Mobile-Optimiert',
                description: 'Über 70 % der Kunden suchen mobil. Ihre Website muss auf dem Smartphone überzeugen – sofort.'
              },
              {
                icon: BarChart3,
                title: 'Positionierung gegen Ihre Konkurrenz',
                description: 'Wir analysieren Ihre Region und platzieren Sie strategisch stärker.'
              }
            ].map((feature, idx) => (
              <Card key={idx} className="hover:shadow-xl transition-all border-l-4 border-cyan-600">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="p-4 bg-cyan-100 rounded-lg">
                      <feature.icon className="w-8 h-8 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-cyan-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Das Resultat: Mehr qualifizierte Anfragen.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Phone, text: 'Mehr Anrufe' },
              { icon: MessageCircle, text: 'Mehr Offertanfragen' },
              { icon: Shield, text: 'Höheres Vertrauen' },
              { icon: Award, text: 'Professioneller Marktauftritt' },
              { icon: TrendingUp, text: 'Weniger Preisdiskussionen' }
            ].map((result, idx) => (
              <Card key={idx} className="bg-white/10 backdrop-blur border-white/20 hover:bg-white/20 transition-all">
                <CardContent className="p-6 text-center">
                  <result.icon className="w-12 h-12 mx-auto mb-3" />
                  <p className="font-semibold">{result.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <p className="text-2xl font-bold">
              Eine gute Website ist kein Kostenpunkt.<br />
              Sie ist ein Umsatzwerkzeug.
            </p>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="funktioniert" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              So <span className="text-cyan-600">funktioniert's</span>
            </h2>
            <p className="text-xl text-gray-600">Einfach. Klar. Professionell.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                step: '1',
                title: 'Kostenlose Beratung',
                description: 'Wir analysieren Ihre aktuelle Situation und Ihre Region.'
              },
              {
                step: '2',
                title: 'Design & Entwicklung',
                description: 'Individuelles Design. SEO-Struktur. Texte, die verkaufen.'
              },
              {
                step: '3',
                title: 'Launch & Unterstützung',
                description: 'Sauberer Go-Live. Einführung. Technische Unterstützung.'
              }
            ].map((item, idx) => (
              <Card key={idx} className="relative hover:shadow-xl transition-all border-t-4 border-cyan-600">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-3xl mb-6 mx-auto">
                    {item.step}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">{item.title}</h3>
                  <p className="text-gray-600 text-center">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="referenzen" className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Resultate unserer <span className="text-cyan-600">Kunden</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              '✅ +60 % mehr Anfragen innerhalb von 3 Monaten',
              '🏆 Top-3 Google-Rankings in lokalen Suchanfragen',
              '📈 Deutlich höhere Abschlussquote bei Offerten',
              '💼 Professioneller Auftritt gegenüber Grosskunden'
            ].map((result, idx) => (
              <Card key={idx} className="border-2 border-green-200 bg-white hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <p className="text-gray-700 font-medium text-center">{result}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="max-w-3xl mx-auto text-center">
            <Card className="bg-cyan-50 border-2 border-cyan-300">
              <CardContent className="p-8">
                <Star className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                <p className="text-2xl font-bold text-gray-900 mb-2">
                  Unsere Kunden berichten vor allem eines:
                </p>
                <p className="text-xl text-cyan-700 italic">
                  „Endlich kommen regelmässig Anfragen über die Website."
                </p>
                <div className="mt-6 flex items-center justify-center gap-2 text-gray-600">
                  <Users className="w-5 h-5" />
                  <span className="font-semibold">Über 80 zufriedene Handwerksbetriebe</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Portfolio / Unsere Referenzen Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-cyan-100 text-cyan-700 hover:bg-cyan-100 px-4 py-2 text-sm font-semibold mb-4">
              Portfolio
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Unsere <span className="text-cyan-600">Referenzen</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Überzeugen Sie sich selbst von unserer Arbeit. Hier eine Auswahl unserer erfolgreich umgesetzten Webprojekte.
            </p>
          </div>

          {/* Portfolio Grid 2x2 */}
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                url: 'https://maraliimmobilien.ch/',
                title: 'Marali Immobilien',
                description: 'Moderne Webpräsenz für Immobilienverwaltung und Bauwesen. Fokus auf klare Strukturen, professionelle Bildsprache und benutzerfreundliche Navigation.',
                category: 'Immobilien'
              },
              {
                url: 'https://www.beni-gipserei.ch/',
                title: 'Beni Gipserei',
                description: 'Digitaler Auftritt für ein traditionsreiches Gipsergeschäft. Ziel war eine klare Präsentation der Handwerksleistungen mit Fokus auf Professionalität.',
                category: 'Handwerk'
              },
              {
                url: 'https://vespa-basel.ch/',
                title: 'Vespa Basel',
                description: 'Stilvoller Webauftritt für Vespa-Liebhaber. Das Design kombiniert italienischen Lifestyle mit einer modernen und intuitiven Benutzerführung.',
                category: 'Lifestyle'
              },
              {
                url: 'https://alpen-facility.ch/',
                title: 'Alpen Facility',
                description: 'Strukturierte Webseite für professionelles Facility Management. Vermittelt Zuverlässigkeit und bietet eine schnelle Übersicht aller Dienstleistungen.',
                category: 'Facility Management'
              }
            ].map((project, idx) => (
              <a
                key={idx}
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block"
                data-testid={`portfolio-card-${idx}`}
              >
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 hover:border-cyan-300">
                  {/* Browser Chrome */}
                  <div className="bg-gray-100 px-4 py-3 border-b border-gray-200 flex items-center gap-3">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-400"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    </div>
                    <div className="flex-1 bg-white rounded-lg px-4 py-1.5 text-sm text-gray-500 font-mono truncate border border-gray-200">
                      {project.url}
                    </div>
                  </div>
                  
                  {/* Website Preview */}
                  <div className="relative aspect-[16/10] overflow-hidden bg-gray-50">
                    <iframe
                      src={project.url}
                      title={project.title}
                      className="absolute inset-0 w-[200%] h-[200%] origin-top-left scale-50 pointer-events-none"
                      loading="lazy"
                      sandbox="allow-scripts allow-same-origin"
                    />
                    {/* Overlay for click handling */}
                    <div className="absolute inset-0 bg-transparent group-hover:bg-cyan-600/5 transition-colors" />
                  </div>

                  {/* Project Info */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-bold text-gray-900 group-hover:text-cyan-600 transition-colors">
                        {project.title}
                      </h3>
                      <Badge variant="outline" className="text-xs font-medium">
                        {project.category}
                      </Badge>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {project.description}
                    </p>
                    <div className="mt-4 flex items-center text-cyan-600 text-sm font-medium group-hover:text-cyan-700">
                      <span>Website besuchen</span>
                      <ChevronRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="preise" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              💰 <span className="text-cyan-600">Transparente Preise</span> für Schweizer Handwerker
            </h2>
            <p className="text-xl text-gray-600 mb-2">
              Einmalzahlung. Volles Eigentum. Keine monatlichen Gebühren.
            </p>
            <p className="text-lg text-gray-500">
              Wählen Sie das Paket, das zu Ihrem Betrieb passt 👇
            </p>
          </div>

          {/* Pricing Cards Grid */}
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-6 mb-12">
            {[
              {
                name: '🌱 BASIC',
                price: '1\'500 – 2\'500',
                targetGroup: 'Ideal für Einzelunternehmer & kleine Betriebe',
                description: 'Perfekt für den Start in die digitale Welt',
                color: 'from-gray-500 to-gray-600',
                borderColor: 'border-gray-300',
                features: [
                  { icon: '📄', title: 'Anzahl Seiten', text: '3–5 Seiten' },
                  { icon: '🎨', title: 'Design', text: 'Modernes Template-Design' },
                  { icon: '📱', title: 'Mobile', text: 'Mobile-Optimierung' },
                  { icon: '🔍', title: 'SEO Basis', text: 'Grundoptimierung' },
                  { icon: '⚙️', title: 'Lokales SEO', text: 'Basis Setup' },
                  { icon: '📝', title: 'Content', text: 'Standard-Texte' },
                  { icon: '📧', title: 'Formulare', text: 'Kontaktformular' },
                  { icon: '🗺️', title: 'Google Maps', text: 'Maps Integration' },
                  { icon: '📊', title: 'Google Business', text: 'Basis-Setup' },
                  { icon: '📸', title: 'Galerie', text: 'Bis zu 10 Bilder' },
                  { icon: '🎯', title: 'Lead-Gen', text: 'Basis CTA' },
                  { icon: '📈', title: 'Analytics', text: 'Google Analytics' },
                  { icon: '💬', title: 'WhatsApp', text: 'WhatsApp Button' },
                  { icon: '🛠️', title: 'Support', text: '1 Monat E-Mail' }
                ]
              },
              {
                name: '⚡ STANDARD',
                price: '3\'500 – 6\'500',
                targetGroup: 'Ideal für etablierte Handwerksbetriebe',
                description: 'Mehr Seiten, besseres SEO, professioneller',
                color: 'from-blue-500 to-blue-600',
                borderColor: 'border-blue-300',
                features: [
                  { icon: '📄', title: 'Anzahl Seiten', text: '6–10 Seiten' },
                  { icon: '🎨', title: 'Design', text: 'Premium individuelles Design' },
                  { icon: '📱', title: 'Mobile', text: 'Mobile-First Optimierung' },
                  { icon: '🔍', title: 'SEO Basis', text: 'Erweiterte Optimierung' },
                  { icon: '⚙️', title: 'Lokales SEO', text: 'Intensive Optimierung' },
                  { icon: '📝', title: 'Content', text: 'Professionelle Texte' },
                  { icon: '📧', title: 'Formulare', text: 'Kontakt + Offert-Formular' },
                  { icon: '🗺️', title: 'Google Maps', text: 'Maps + Bewertungen' },
                  { icon: '📊', title: 'Google Business', text: 'Vollständige Optimierung' },
                  { icon: '📸', title: 'Galerie', text: 'Bis zu 30 Bilder + Referenzen' },
                  { icon: '🎯', title: 'Lead-Gen', text: 'Optimierte CTAs' },
                  { icon: '📈', title: 'Analytics', text: 'Analytics + Tracking' },
                  { icon: '💬', title: 'WhatsApp', text: 'WhatsApp + Messenger' },
                  { icon: '🛠️', title: 'Support', text: '3 Monate Priority Support' }
                ]
              },
              {
                name: '🚀 PRO',
                price: '7\'500 – 12\'000',
                targetGroup: 'Ideal für wachsende Betriebe & Regionalführer',
                description: 'Maximale Sichtbarkeit & Lead-Generierung',
                color: 'from-cyan-600 to-purple-600',
                borderColor: 'border-cyan-400',
                recommended: true,
                features: [
                  { icon: '📄', title: 'Anzahl Seiten', text: '10–20 Seiten unbegrenzt' },
                  { icon: '🎨', title: 'Design', text: 'Premium Design + Animationen' },
                  { icon: '📱', title: 'Mobile', text: 'Progressive Web App (PWA)' },
                  { icon: '🔍', title: 'SEO Basis', text: 'Erweiterte SEO-Strategie' },
                  { icon: '⚙️', title: 'Lokales SEO', text: 'Multi-Standorte Optimierung' },
                  { icon: '📝', title: 'Content', text: 'Verkaufsstarke Texte + Blog' },
                  { icon: '📧', title: 'Formulare', text: 'CRM-Integration' },
                  { icon: '🗺️', title: 'Google Maps', text: 'Multi-Location Setup' },
                  { icon: '📊', title: 'Google Business', text: 'Volloptimierung + Monitoring' },
                  { icon: '📸', title: 'Galerie', text: 'Unbegrenzt + Video-Integration' },
                  { icon: '🎯', title: 'Lead-Gen', text: 'A/B Testing + Funnels' },
                  { icon: '📈', title: 'Analytics', text: 'Advanced Analytics + Reports' },
                  { icon: '💬', title: 'WhatsApp', text: 'WhatsApp Business API' },
                  { icon: '🛠️', title: 'Support', text: '6 Monate VIP Support + Wartung' }
                ]
              },
              {
                name: '👑 PREMIUM',
                price: '15\'000 – 25\'000',
                targetGroup: 'Ideal für Marktführer & Grosskunden',
                description: 'Komplettlösung mit laufender Betreuung',
                color: 'from-purple-600 to-pink-600',
                borderColor: 'border-purple-400',
                features: [
                  { icon: '📄', title: 'Anzahl Seiten', text: 'Unbegrenzt + Kundenportal' },
                  { icon: '🎨', title: 'Design', text: 'Exklusiv-Design + 3D + Video' },
                  { icon: '📱', title: 'Mobile', text: 'Native App iOS/Android' },
                  { icon: '🔍', title: 'SEO Basis', text: 'Full-Service SEO-Betreuung' },
                  { icon: '⚙️', title: 'Lokales SEO', text: 'Regionale Dominanz-Strategie' },
                  { icon: '📝', title: 'Content', text: 'Premium Content + PR + Social' },
                  { icon: '📧', title: 'Formulare', text: 'Vollautomatisierung + AI' },
                  { icon: '🗺️', title: 'Google Maps', text: 'Enterprise Multi-Location' },
                  { icon: '📊', title: 'Google Business', text: 'Full Management + Ads' },
                  { icon: '📸', title: 'Galerie', text: 'Profi-Fotoshootings inklusive' },
                  { icon: '🎯', title: 'Lead-Gen', text: 'Conversion-Optimierung + AI' },
                  { icon: '📈', title: 'Analytics', text: 'Custom Dashboards + BI' },
                  { icon: '💬', title: 'WhatsApp', text: 'WhatsApp + Chatbot AI' },
                  { icon: '🛠️', title: 'Support', text: '12 Monate Dedicated Manager' }
                ]
              }
            ].map((plan, idx) => (
              <Card 
                key={idx} 
                className={`relative hover:shadow-2xl transition-all ${
                  plan.recommended 
                    ? 'border-4 border-cyan-500 shadow-2xl transform scale-105 lg:scale-110' 
                    : `border-2 ${plan.borderColor}`
                }`}
              >
                {plan.recommended && (
                  <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 z-10">
                    <Badge className="bg-gradient-to-r from-cyan-600 to-purple-600 text-white px-6 py-2 text-base font-bold shadow-xl animate-pulse">
                      ⭐ EMPFOHLEN
                    </Badge>
                  </div>
                )}
                <CardContent className="p-6">
                  {/* Header */}
                  <div className={`text-center mb-4 p-4 rounded-xl bg-gradient-to-br ${plan.color} text-white`}>
                    <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                    <div className="text-3xl font-bold mb-1">
                      CHF {plan.price}
                    </div>
                    <p className="text-sm opacity-90">Einmalzahlung</p>
                  </div>

                  {/* Target Group */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg mb-4">
                    <p className="text-sm font-bold text-gray-900 mb-1">{plan.targetGroup}</p>
                    <p className="text-xs text-gray-600">{plan.description}</p>
                  </div>

                  {/* All Features Included */}
                  <div className="bg-green-50 border-2 border-green-300 rounded-lg p-3 mb-4">
                    <p className="text-xs font-bold text-green-700 text-center flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Alle Features inklusive
                    </p>
                  </div>

                  {/* Features with Details */}
                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-start gap-2 bg-white p-2 rounded-lg shadow-sm hover:shadow-md transition-all">
                        <div className="flex items-start gap-2 flex-1">
                          <span className="text-xl flex-shrink-0">{feature.icon}</span>
                          <div className="flex-1">
                            <p className="text-xs font-bold text-gray-900">{feature.title}</p>
                            <p className="text-xs text-gray-600">{feature.text}</p>
                          </div>
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                        </div>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button 
                    onClick={scrollToContact}
                    className={`w-full py-6 text-base font-bold ${
                      plan.recommended 
                        ? 'bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700 text-white shadow-xl' 
                        : 'bg-gray-900 hover:bg-gray-800 text-white'
                    }`}
                  >
                    🎯 Jetzt Offerte anfordern
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Why PRO Package */}
          <Card className="bg-gradient-to-br from-cyan-50 to-purple-50 border-4 border-cyan-300 max-w-5xl mx-auto mb-12 shadow-2xl">
            <CardContent className="p-8">
              <div className="text-center">
                <div className="text-6xl mb-4">🎯</div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  Warum wählen die meisten das PRO-Paket?
                </h3>
                <p className="text-xl text-gray-700 mb-6">
                  Das <span className="font-bold text-cyan-700">PRO-Paket</span> bietet das beste Preis-Leistungs-Verhältnis für Handwerksbetriebe, die online wachsen wollen.
                </p>
                <div className="grid md:grid-cols-3 gap-6 mt-8">
                  {[
                    { icon: '📈', title: 'Maximale Sichtbarkeit', text: 'Top-Rankings in Ihrer Region' },
                    { icon: '💰', title: 'Beste Investition', text: 'ROI in 6-12 Monaten' },
                    { icon: '🚀', title: 'Volle Features', text: 'Alles für Lead-Generierung' }
                  ].map((item, idx) => (
                    <div key={idx} className="bg-white p-6 rounded-xl shadow-lg">
                      <div className="text-4xl mb-3">{item.icon}</div>
                      <h4 className="font-bold text-gray-900 mb-2">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Add-ons */}
          <div className="max-w-5xl mx-auto">
            <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              ➕ <span className="text-cyan-600">Optional buchbare</span> Zusatzleistungen
            </h3>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                { icon: '📸', name: 'Professionelles Fotoshooting', price: 'ab CHF 990.–', description: 'Vor Ort beim Kunden' },
                { icon: '🌍', name: 'Mehrere Standorte SEO', price: 'ab CHF 490.– pro Standort', description: 'Lokale Optimierung' },
                { icon: '⚡', name: 'Express-Umsetzung', price: 'CHF 1\'500.–', description: 'Fertig in 2 Wochen' },
                { icon: '📱', name: 'Social Media Setup', price: 'CHF 790.–', description: 'Facebook, Instagram, LinkedIn' },
                { icon: '🎬', name: 'Professionelles Video', price: 'ab CHF 1\'900.–', description: 'Imagefilm 60-90 Sek.' },
                { icon: '📝', name: 'Monatlicher Blog-Content', price: 'CHF 390.– /Monat', description: '2 SEO-optimierte Artikel' }
              ].map((addon, idx) => (
                <Card key={idx} className="border-2 border-gray-200 hover:border-cyan-400 hover:shadow-xl transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="text-5xl mb-3">{addon.icon}</div>
                    <p className="font-bold text-gray-900 mb-2">{addon.name}</p>
                    <p className="text-cyan-600 font-bold text-lg mb-2">{addon.price}</p>
                    <p className="text-xs text-gray-500">{addon.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* What's Always Included */}
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-4 border-green-300 shadow-xl">
              <CardContent className="p-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                  In allen Paketen immer inklusive
                </h4>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    '✅ Keine monatlichen Kosten',
                    '✅ Volles Eigentum',
                    '✅ SSL-Zertifikat',
                    '✅ DSGVO-konform',
                    '✅ Hosting-Empfehlung',
                    '✅ Einführung & Schulung',
                    '✅ Transparente Einmalzahlung',
                    '✅ Alle Zugänge & Rechte'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-white p-3 rounded-lg shadow-sm">
                      <span className="text-sm font-medium text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Häufige <span className="text-cyan-600">Fragen</span>
            </h2>
          </div>
          <div className="space-y-6">
            {[
              {
                question: 'Gibt es versteckte Kosten?',
                answer: 'Nein. Sie zahlen einmalig den vereinbarten Betrag. Keine Abos. Keine Lizenzgebühren.'
              },
              {
                question: 'Bleibt die Website mein Eigentum?',
                answer: 'Ja. Zu 100 %. Sie erhalten alle Zugänge und Rechte.'
              },
              {
                question: 'Ist SEO wirklich inklusive?',
                answer: 'Ja. Jede Website wird technisch und lokal optimiert. Je nach Paket unterschiedlich intensiv.'
              },
              {
                question: 'Wie lange dauert die Umsetzung?',
                answer: 'In der Regel 3–6 Wochen, abhängig vom Paket und Ihrer Rückmeldung.'
              },
              {
                question: 'Gibt es Support?',
                answer: 'Ja. Nach dem Launch erhalten Sie Unterstützung und Einführung.'
              }
            ].map((faq, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ihre Konkurrenz schläft nicht.
          </h2>
          <p className="text-xl mb-8">
            Wir nehmen pro Monat nur eine <span className="font-bold text-cyan-400">begrenzte Anzahl Projekte</span> an,<br />
            um Qualität und Resultate zu garantieren.
          </p>
          <div className="bg-white/10 backdrop-blur rounded-xl p-8 mb-8">
            <p className="text-2xl font-bold mb-6">Wenn Sie:</p>
            <div className="space-y-3">
              {[
                'Mehr Anfragen wollen',
                'Online dominieren wollen',
                'Professionell auftreten wollen'
              ].map((item, idx) => (
                <div key={idx} className="flex items-center justify-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-cyan-400" />
                  <span className="text-xl">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-2xl font-bold mt-6">Dann ist jetzt der richtige Zeitpunkt.</p>
          </div>
          <Button 
            onClick={scrollToContact}
            size="lg"
            className="bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700 text-white px-12 py-8 text-2xl rounded-lg shadow-2xl hover:shadow-cyan-500/50 transition-all"
          >
            <ChevronRight className="w-8 h-8 mr-3" />
            Jetzt kostenlose Offerte sichern
          </Button>
          <p className="text-sm text-gray-400 mt-6">
            Begrenzte Kapazitäten pro Monat. Kostenlos. Unverbindlich. Strategisch wertvoll.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontakt" className="py-20 px-6 bg-gradient-to-br from-cyan-50 to-purple-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Jetzt Anfrage senden und lokale Marktposition stärken.
            </h2>
            <p className="text-xl text-gray-600">
              Kostenlose Beratung. Unverbindlich. Strategisch wertvoll.
            </p>
          </div>
          <ContactForm />
        </div>
      </section>
    </div>
  );
};

export default Home;
